import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { designTokens, useTheme } from '../../theme';
import Button from '../Button';
import type { Language } from '../LanguageSelector/LanguageSelector';

import iconMultiHeader from '../../../assets/header/Icon-multi-header.png';
import { LANGUAGE_STORAGE_KEY } from '../../i18n';

export interface MenuItem {
  /** Chave única (ex.: path) */
  id?: string;
  label: string;
  icon: ReactNode;
  /** URL para navegação (usado quando não há renderItem) */
  href: string;
  /** Se o item está ativo (estilo destacado) */
  active?: boolean;
  /** Path para uso com React Router (opcional, usado pelo consumer) */
  to?: string;
}

/** Item de um dropdown no header (ex.: Sair, Perfil) */
export interface HeaderActionMenuItem {
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
  href?: string;
}

/** Ação do header: botão simples (onClick) ou dropdown (menuItems). Quem chama passa label e icon. */
export interface HeaderAction {
  key?: string;
  label: ReactNode;
  icon?: ReactNode;
  onClick?: () => void;
  menuItems?: HeaderActionMenuItem[];
}

/** Opção do seletor "View As" no header */
export interface ViewAsOption {
  value: string;
  label: string;
}

/** Seletor "View As" no header: label + ícone + botão com valor atual que abre dropdown de opções clicáveis */
export interface HeaderViewAsAction {
  /** Texto à esquerda (ex.: "View As:") */
  label: string;
  /** Ícone ao lado do label (ex.: ícone de olho) */
  icon?: ReactNode;
  /** Opções do dropdown */
  options: ViewAsOption[];
  /** Valor atualmente selecionado */
  value: string;
  /** Chamado ao selecionar uma opção */
  onChange: (value: string) => void;
}

export interface AppLayoutProps {
  /** Logo no topo da sidebar (imagem URL, <img> ou qualquer ReactNode). Omitir para não exibir logo */
  logo?: ReactNode;
  /** Itens do menu lateral */
  menuItems: MenuItem[];
  /** Conteúdo do link do item (para usar React Router Link). Recebe (item, content, style). Se não informado, usa <a href> */
  renderMenuItem?: (
    item: MenuItem,
    content: ReactNode,
    itemStyle: React.CSSProperties
  ) => ReactNode;
  /** Título exibido no cabeçalho (ex.: "User Management") */
  headerTitle: string;
  /** Ícone opcional ao lado do título (ex.: ícone do item de menu selecionado). Quando um botão/item é clicado, o consumer pode passar icon + headerTitle. */
  headerTitleIcon?: ReactNode;
  /** Quando definido, substitui título e ícone à esquerda (ex.: barra de pesquisa). A área recebe flex para ocupar o espaço disponível. */
  headerTitleContent?: ReactNode;
  /** Ícone de configuração ao lado do View As (opcional). Renderizado antes do headerViewAs. */
  headerConfigIcon?: { icon: ReactNode; onClick: () => void };
  /** Seletor "View As" no header (label + botão com valor atual + dropdown). Renderizado antes dos headerActions. */
  headerViewAs?: HeaderViewAsAction;
  /** Botões do header. Quem chama passa label e icon para cada um; cada item pode ser botão (onClick) ou dropdown (menuItems). */
  headerActions?: HeaderAction[];
  /** Texto de copyright no rodapé da sidebar (ex.: "© 2026 FERA Diagnostics") */
  copyright?: string;
  /** Se true, oculta o menu lateral (sidebar). Com `logo` definido, a logo é exibida à esquerda do header. */
  hideSidebar?: boolean;
  /** Identificador de teste para o layout (container raiz) */
  dataE2eLayout?: string;
  /** Identificador de teste para o header */
  dataE2eHeader?: string;
  /** Identificador de teste para o conteúdo principal */
  dataE2eMain?: string;
  children: ReactNode;
}

export default function AppLayout({
  logo,
  menuItems,
  renderMenuItem,
  headerTitle,
  headerTitleIcon,
  headerTitleContent,
  headerViewAs,
  headerActions = [],
  copyright = '',
  hideSidebar = false,
  dataE2eLayout,
  dataE2eHeader,
  dataE2eMain,
  children,
}: AppLayoutProps) {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const layout = theme.appLayout ?? designTokens.appLayout;
  const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null);
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);

  const headerButtonStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    fontWeight: theme.typography.fontWeight.normal,
  };

  /** Círculo amarelo com borda para ícones do header (ex.: usuário, View As) */
  const headerIconCircleStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    border: '2px solid rgba(255, 255, 255, 0.35)',
    color: theme.palette.primary.contrastText,
    flexShrink: 0,
  };

  const sidebarStyle: React.CSSProperties = {
    width: theme.layout.sidebarWidth,
    minHeight: '100vh',
    backgroundColor: layout.sidebarBg,
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
  };

  const logoStyle: React.CSSProperties = {
    paddingTop: theme.spacing.md,
    paddingBottom: theme.spacing.lg,
    paddingRight: theme.spacing['2xl'],
    paddingLeft: theme.spacing['2xl'],
    borderBottom: `1px solid rgba(255,255,255,0.1)`,
    minHeight: 56,
    minWidth: 139,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const navStyle: React.CSSProperties = {
    flex: 1,
    padding: theme.spacing.sm,
    paddingTop: theme.spacing.md,
  };

  const itemBaseStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing.md,
    borderRadius: theme.shape.borderRadius.md,
    marginBottom: theme.spacing.sm,
    marginRight: theme.spacing.sm,
    marginLeft: theme.spacing.sm,
    color: layout.menuItemText,
    textDecoration: 'none',
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.md,
    fontWeight: theme.typography.fontWeight.semibold,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
  };

  const copyrightStyle: React.CSSProperties = {
    padding: theme.spacing.md,
    color: layout.copyrightText,
    fontSize: theme.typography.fontSize.xs,
    textAlign: 'center',
    fontWeight: theme.typography.fontWeight.normal,
    fontFamily: theme.typography.fontFamily,
  };

  const headerStyle: React.CSSProperties = {
    // Mesma altura do admin quando há sidebar OU quando a logo vai no header (aluno sem menu lateral).
    height:
      hideSidebar && logo == null
        ? theme.layout.headerHeight
        : theme.layout.headerHeightWithSidebar,
    backgroundColor: layout.headerBg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: theme.spacing.lg,
    paddingRight: theme.spacing.lg,
    flexShrink: 0,
    boxSizing: 'border-box',
  };

  const headerTitleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.semibold,
    color: layout.headerTitleText,
  };

  const mainStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    overflow: 'auto',
    backgroundColor: theme.palette.background.default,
    padding: 0,
    margin: 0,
    width: '100%',
    boxSizing: 'border-box',
  };

  const languageOptions: Array<{ value: Language; label: 'EN' | 'ES' }> = [
    { value: 'en-US', label: 'EN' },
    { value: 'es', label: 'ES' },
  ];

  const currentLanguage: Language = (() => {
    const raw = i18n.language ?? 'en-US';
    if (raw === 'es') return 'es';
    if (raw.startsWith('en')) return 'en-US';
    return 'es';
  })();

  const toggleLanguageOpen = () => {
    setViewAsOpen(false);
    setOpenDropdownKey(null);
    setLanguageOpen(o => !o);
  };

  return (
    <div
      style={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}
      data-e2e={dataE2eLayout}
    >
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {!hideSidebar && (
          <aside style={sidebarStyle} className="e-learning-app-layout-sidebar">
            {logo != null && (
              <div style={logoStyle}>
                {typeof logo === 'string' ? (
                  <img
                    src={logo}
                    alt=""
                    style={{ maxWidth: '100%', height: 'auto' }}
                  />
                ) : (
                  logo
                )}
              </div>
            )}
            <nav style={navStyle} aria-label="Menu principal">
              {menuItems.map(item => {
                const isActive = item.active;
                const itemWrapperStyle: React.CSSProperties = {
                  ...itemBaseStyle,
                  ...(isActive
                    ? {
                        backgroundColor: layout.menuItemActiveBg,
                        color: layout.menuItemActiveText ?? layout.menuItemText,
                      }
                    : {}),
                };
                const content = (
                  <>
                    <span
                      style={{
                        display: 'flex',
                        flexShrink: 0,
                        marginRight: theme.spacing.sm,
                      }}
                    >
                      {item.icon}
                    </span>
                    <span>{item.label}</span>
                  </>
                );
                const linkContent = renderMenuItem ? (
                  renderMenuItem(item, content, itemWrapperStyle)
                ) : (
                  <a href={item.href} style={itemWrapperStyle}>
                    {content}
                  </a>
                );
                return (
                  <div
                    key={item.id ?? item.href}
                    style={{ marginBottom: theme.spacing.xs }}
                  >
                    {linkContent}
                  </div>
                );
              })}
            </nav>
            {copyright ? <div style={copyrightStyle}>{copyright}</div> : null}
          </aside>
        )}

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
          }}
        >
          <header
            style={headerStyle}
            className="e-learning-app-layout-header"
            data-e2e={dataE2eHeader}
          >
            {hideSidebar && logo != null ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing.md,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    marginLeft: theme.spacing.xl,
                  }}
                >
                  {typeof logo === 'string' ? (
                    <img
                      src={logo}
                      alt=""
                      style={{ maxHeight: 40, width: 'auto' }}
                    />
                  ) : (
                    logo
                  )}
                </div>
                <div
                  style={
                    headerTitleContent != null
                      ? {
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: theme.spacing.sm,
                          flex: 1,
                          minWidth: 0,
                          fontFamily: theme.typography.fontFamily,
                        }
                      : {
                          ...headerTitleStyle,
                          display: 'flex',
                          alignItems: 'center',
                          gap: theme.spacing.sm,
                          flex: 1,
                          minWidth: 0,
                        }
                  }
                >
                  {headerTitleContent != null ? (
                    headerTitleContent
                  ) : (
                    <>
                      {headerTitleIcon != null && (
                        <span style={{ display: 'flex', flexShrink: 0 }}>
                          {headerTitleIcon}
                        </span>
                      )}
                      <span>{headerTitle}</span>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div
                style={
                  headerTitleContent != null
                    ? {
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing.sm,
                        flex: 1,
                        minWidth: 0,
                        fontFamily: theme.typography.fontFamily,
                      }
                    : {
                        ...headerTitleStyle,
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing.sm,
                      }
                }
              >
                {headerTitleContent != null ? (
                  headerTitleContent
                ) : (
                  <>
                    {headerTitleIcon != null && (
                      <span style={{ display: 'flex', flexShrink: 0 }}>
                        {headerTitleIcon}
                      </span>
                    )}
                    <span>{headerTitle}</span>
                  </>
                )}
              </div>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.md,
              }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: theme.spacing.sm,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                  borderRadius: theme.shape.borderRadius.md,
                }}
              >
                <span
                  style={{
                    display: 'flex',
                    flexShrink: 0,
                    color: layout.headerTitleText,
                  }}
                >
                  <img
                    src={iconMultiHeader}
                    alt=""
                    aria-hidden
                    style={{
                      width: 20,
                      height: 20,
                      display: 'block',
                    }}
                  />
                </span>
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={toggleLanguageOpen}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: theme.spacing.sm,
                      background: 'transparent',
                      color: theme.palette.text.white,
                      paddingTop: '4px',
                      paddingBottom: '4px',
                      paddingLeft: theme.spacing.sm,
                      paddingRight: theme.spacing.sm,
                      border: 'none',
                      borderRadius: theme.shape.borderRadius.sm,
                      fontSize: theme.typography.fontSize.sm,
                      fontWeight: theme.typography.fontWeight.medium,
                      fontFamily: theme.typography.fontFamily,
                      cursor: 'pointer',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    }}
                    aria-expanded={languageOpen}
                    aria-haspopup="listbox"
                    aria-label={`Language ${currentLanguage}`}
                  >
                    {languageOptions.find(o => o.value === currentLanguage)
                      ?.label ?? currentLanguage}
                  </button>

                  {languageOpen && (
                    <>
                      <div
                        role="presentation"
                        style={{
                          position: 'fixed',
                          inset: 0,
                          zIndex: 10,
                        }}
                        onClick={() => setLanguageOpen(false)}
                      />
                      <ul
                        role="listbox"
                        aria-label="Language"
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 'calc(100% + 4px)',
                          minWidth: '120px',
                          padding: '4px',
                          margin: 0,
                          background: theme.palette.background.paper,
                          borderRadius: theme.shape.borderRadius.sm,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          listStyle: 'none',
                          zIndex: 11,
                        }}
                      >
                        {languageOptions.map(opt => {
                          const isSelected = opt.value === currentLanguage;
                          return (
                            <li
                              key={opt.value}
                              role="option"
                              aria-selected={isSelected}
                              style={{ marginBottom: '2px' }}
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  try {
                                    window.localStorage.setItem(
                                      LANGUAGE_STORAGE_KEY,
                                      opt.value
                                    );
                                  } catch {
                                    // ignore
                                  }
                                  i18n.changeLanguage(opt.value);
                                  setLanguageOpen(false);
                                }}
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                  textAlign: 'center',
                                  backgroundColor: 'transparent',
                                  color: isSelected
                                    ? theme.palette.primary.main
                                    : theme.palette.text.primary,
                                  border: 'none',
                                  borderRadius: theme.shape.borderRadius.sm,
                                  fontSize: theme.typography.fontSize.sm,
                                  fontWeight:
                                    theme.typography.fontWeight.medium,
                                  fontFamily: theme.typography.fontFamily,
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s',
                                }}
                              >
                                {opt.label}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  )}
                </div>
              </div>
              {headerViewAs != null && (
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: theme.spacing.sm,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                    borderRadius: theme.shape.borderRadius.md,
                  }}
                >
                  {headerViewAs.icon != null && (
                    <span
                      style={{
                        display: 'flex',
                        flexShrink: 0,
                        color: layout.headerTitleText,
                      }}
                    >
                      {headerViewAs.icon}
                    </span>
                  )}
                  {headerViewAs.label?.trim() ? (
                    <span
                      style={{
                        color: layout.headerTitleText,
                        fontSize: theme.typography.fontSize.sm,
                        fontWeight: theme.typography.fontWeight.normal,
                        fontFamily: theme.typography.fontFamily,
                      }}
                    >
                      {headerViewAs.label}
                    </span>
                  ) : null}
                  <div style={{ position: 'relative' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setLanguageOpen(false);
                        setOpenDropdownKey(null);
                        setViewAsOpen(o => !o);
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: theme.spacing.sm,
                        background: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        paddingTop: '6px',
                        paddingBottom: '6px',
                        paddingLeft: theme.spacing.md,
                        paddingRight: theme.spacing.md,
                        border: 'none',
                        borderRadius: theme.shape.borderRadius.sm,
                        fontSize: theme.typography.fontSize.sm,
                        fontWeight: theme.typography.fontWeight.medium,
                        fontFamily: theme.typography.fontFamily,
                        cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      }}
                      aria-expanded={viewAsOpen}
                      aria-haspopup="listbox"
                      aria-label={`${
                        headerViewAs.label?.trim()
                          ? headerViewAs.label
                          : 'View As'
                      } ${headerViewAs.value}`}
                    >
                      {headerViewAs.options.find(
                        o => o.value === headerViewAs.value
                      )?.label ?? headerViewAs.value}
                      <svg
                        width={14}
                        height={14}
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden
                        style={{
                          transition: 'transform 0.2s',
                          transform: viewAsOpen ? 'rotate(180deg)' : 'none',
                        }}
                      >
                        <path d="M7 10l5 5 5-5z" />
                      </svg>
                    </button>
                    {viewAsOpen && (
                      <>
                        <div
                          role="presentation"
                          style={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: 10,
                          }}
                          onClick={() => setViewAsOpen(false)}
                        />
                        <ul
                          role="listbox"
                          aria-label={
                            headerViewAs.label?.trim()
                              ? headerViewAs.label
                              : 'View As'
                          }
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 'calc(100% + 4px)',
                            minWidth: '120px',
                            padding: '4px',
                            margin: 0,
                            background: theme.palette.background.paper,
                            borderRadius: theme.shape.borderRadius.sm,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            listStyle: 'none',
                            zIndex: 11,
                          }}
                        >
                          {headerViewAs.options.map(opt => {
                            const isSelected = opt.value === headerViewAs.value;
                            return (
                              <li
                                key={opt.value}
                                role="option"
                                aria-selected={isSelected}
                                style={{ marginBottom: '2px' }}
                              >
                                <button
                                  type="button"
                                  onClick={() => {
                                    headerViewAs.onChange(opt.value);
                                    setViewAsOpen(false);
                                  }}
                                  style={{
                                    display: 'block',
                                    width: '100%',
                                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                    textAlign: 'center',
                                    backgroundColor: isSelected
                                      ? theme.palette.primary.main
                                      : theme.palette.background.paper,
                                    color: isSelected
                                      ? theme.palette.primary.contrastText
                                      : theme.palette.text.primary,
                                    border: 'none',
                                    borderRadius: theme.shape.borderRadius.sm,
                                    fontSize: theme.typography.fontSize.sm,
                                    fontWeight:
                                      theme.typography.fontWeight.medium,
                                    fontFamily: theme.typography.fontFamily,
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s',
                                  }}
                                  onMouseEnter={e => {
                                    if (!isSelected) {
                                      e.currentTarget.style.backgroundColor =
                                        '#f3f4f6';
                                    }
                                  }}
                                  onMouseLeave={e => {
                                    if (!isSelected) {
                                      e.currentTarget.style.backgroundColor =
                                        theme.palette.background.paper;
                                    }
                                  }}
                                >
                                  {opt.label}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              )}
              {headerActions.map((action, index) => {
                const key = action.key ?? `header-action-${index}`;
                const isDropdown =
                  action.menuItems && action.menuItems.length > 0;
                const isOpen = openDropdownKey === key;

                const triggerButton = (
                  <Button
                    type="button"
                    onClick={
                      isDropdown
                        ? () => {
                            setLanguageOpen(false);
                            setViewAsOpen(false);
                            setOpenDropdownKey(isOpen ? null : key);
                          }
                        : () => {
                            setLanguageOpen(false);
                            setViewAsOpen(false);
                            action.onClick?.();
                          }
                    }
                    size="small"
                    backgroundColor="transparent"
                    color={layout.headerTitleText}
                    style={headerButtonStyle}
                    aria-expanded={isDropdown ? isOpen : undefined}
                    aria-haspopup={isDropdown ? 'true' : undefined}
                  >
                    {action.icon != null && (
                      <span style={headerIconCircleStyle}>{action.icon}</span>
                    )}
                    {action.label}
                  </Button>
                );

                if (isDropdown) {
                  return (
                    <div key={key} style={{ position: 'relative' }}>
                      {triggerButton}
                      {isOpen && (
                        <>
                          <div
                            role="presentation"
                            style={{ position: 'fixed', inset: 0, zIndex: 10 }}
                            onClick={() => setOpenDropdownKey(null)}
                          />
                          <ul
                            role="menu"
                            style={{
                              position: 'absolute',
                              right: 0,
                              top: '100%',
                              marginTop: theme.spacing.xs,
                              minWidth: 160,
                              padding: theme.spacing.xs,
                              background: theme.palette.background.paper,
                              borderRadius: theme.shape.borderRadius.sm,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                              border: `1px solid ${theme.palette.border?.main ?? '#e5e7eb'}`,
                              listStyle: 'none',
                              zIndex: 11,
                            }}
                          >
                            {action.menuItems!.map((mi, i) => {
                              const itemStyle: React.CSSProperties = {
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing.sm,
                                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                color: theme.palette.text.primary,
                                fontFamily: theme.typography.fontFamily,
                                fontSize: theme.typography.fontSize.sm,
                                cursor: 'pointer',
                                border: 'none',
                                background: 'transparent',
                                width: '100%',
                                textAlign: 'left',
                                borderRadius: theme.shape.borderRadius.sm,
                              };
                              return (
                                <li key={i} role="none">
                                  {mi.href ? (
                                    <a
                                      href={mi.href}
                                      role="menuitem"
                                      style={{
                                        ...itemStyle,
                                        textDecoration: 'none',
                                      }}
                                    >
                                      {mi.icon != null && (
                                        <span
                                          style={{
                                            display: 'flex',
                                            flexShrink: 0,
                                          }}
                                        >
                                          {mi.icon}
                                        </span>
                                      )}
                                      {mi.label}
                                    </a>
                                  ) : (
                                    <button
                                      type="button"
                                      role="menuitem"
                                      onClick={() => {
                                        mi.onClick?.();
                                        setOpenDropdownKey(null);
                                      }}
                                      style={itemStyle}
                                      onMouseEnter={e => {
                                        e.currentTarget.style.backgroundColor =
                                          theme.palette.background.default;
                                      }}
                                      onMouseLeave={e => {
                                        e.currentTarget.style.backgroundColor =
                                          'transparent';
                                      }}
                                    >
                                      {mi.icon != null && (
                                        <span
                                          style={{
                                            display: 'flex',
                                            flexShrink: 0,
                                          }}
                                        >
                                          {mi.icon}
                                        </span>
                                      )}
                                      {mi.label}
                                    </button>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </>
                      )}
                    </div>
                  );
                }

                return <span key={key}>{triggerButton}</span>;
              })}
            </div>
          </header>
          <main
            style={mainStyle}
            className="e-learning-app-layout-main"
            data-e2e={dataE2eMain}
          >
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
